import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { localStoreAPI, localSyncAPI } from '@/services/localApi';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';
import {
  Monitor, Plus, Settings, RefreshCw, Server, Wifi, WifiOff,
  ArrowLeft, CheckCircle, AlertCircle, Trash2
} from 'lucide-react';

const LocalStoreSettings = () => {
  const navigate = useNavigate();
  const [storeInfo, setStoreInfo] = useState(null);
  const [terminals, setTerminals] = useState([]);
  const [syncStatus, setSyncStatus] = useState(null);
  const [loading, setLoading] = useState(true);

  // Add Terminal Modal
  const [showAddModal, setShowAddModal] = useState(false);
  const [newTerminalName, setNewTerminalName] = useState('');
  const [newTerminalType, setNewTerminalType] = useState('pos');
  const [adding, setAdding] = useState(false);

  // Sync states
  const [syncing, setSyncing] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [storeRes, terminalsRes, statusRes] = await Promise.all([
        localStoreAPI.getStoreInfo(),
        localSyncAPI.getTerminals(),
        localSyncAPI.getStatus()
      ]);

      setStoreInfo(storeRes.data);
      setTerminals(terminalsRes.data.terminals || []);
      setSyncStatus(statusRes.data);
    } catch (error) {
      console.error('Failed to load data:', error);
      toast.error('Failed to connect to local server');
    } finally {
      setLoading(false);
    }
  };

  const handleAddTerminal = async () => {
    if (!newTerminalName.trim()) {
      toast.error('Please enter a terminal name');
      return;
    }

    if (terminals.length >= 5) {
      toast.error('Maximum 5 terminals allowed per store');
      return;
    }

    try {
      setAdding(true);
      const res = await localSyncAPI.registerTerminal(newTerminalName, newTerminalType);
      
      toast.success(`Terminal ${res.data.terminal_id} registered!`);
      setShowAddModal(false);
      setNewTerminalName('');
      setNewTerminalType('pos');
      loadData();
    } catch (error) {
      console.error('Failed to add terminal:', error);
      toast.error(error.response?.data?.detail || 'Failed to register terminal');
    } finally {
      setAdding(false);
    }
  };

  const handleSyncPull = async () => {
    setSyncing(true);
    try {
      const res = await localSyncAPI.pullFromHQ();
      const result = res.data.result || {};
      
      toast.success(
        `Synced: ${result.products || 0} products, ${result.categories || 0} categories, ${result.brands || 0} brands`
      );
      loadData();
    } catch (error) {
      toast.error('Failed to sync from HQ');
    } finally {
      setSyncing(false);
    }
  };

  const handleSyncPush = async () => {
    setSyncing(true);
    try {
      const res = await localSyncAPI.pushToHQ();
      const result = res.data.result || {};
      
      if (result.sales > 0 || result.registers > 0) {
        toast.success(`Pushed: ${result.sales || 0} sales, ${result.registers || 0} registers`);
      } else {
        toast.info('Nothing to push - all data is synced');
      }
      loadData();
    } catch (error) {
      toast.error('Failed to sync to HQ');
    } finally {
      setSyncing(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="text-white">Loading...</div>
      </div>
    );
  }

  if (!storeInfo?.configured) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center p-8">
        <Card className="max-w-md w-full">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <AlertCircle className="w-5 h-5 text-yellow-500" />
              Store Not Configured
            </CardTitle>
            <CardDescription>
              The local server needs to be initialized with HQ first.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-gray-600 mb-4">
              Please run the initialization command to connect this local server to HQ:
            </p>
            <pre className="bg-gray-100 p-3 rounded text-xs overflow-x-auto">
{`curl -X POST http://localhost:8002/api/sync/initialize \\
  -H "Content-Type: application/json" \\
  -d '{
    "hq_url": "YOUR_HQ_URL",
    "store_code": "YOUR_STORE_CODE",
    "store_secret": "YOUR_STORE_SECRET",
    "device_id": "local-server-001"
  }'`}
            </pre>
            <Button 
              className="mt-4 w-full" 
              onClick={() => navigate('/local-store')}
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 p-8">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            className="text-gray-400 hover:text-white"
            onClick={() => navigate('/local-store')}
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-white">Local Store Settings</h1>
            <p className="text-gray-400">Manage terminals and sync configuration</p>
          </div>
        </div>
        
        {/* Online Status */}
        <div className={`flex items-center gap-2 px-4 py-2 rounded-full ${
          syncStatus?.is_online ? 'bg-green-500/20' : 'bg-red-500/20'
        }`}>
          {syncStatus?.is_online ? (
            <>
              <Wifi className="w-5 h-5 text-green-400" />
              <span className="text-green-400 font-medium">Online</span>
            </>
          ) : (
            <>
              <WifiOff className="w-5 h-5 text-red-400" />
              <span className="text-red-400 font-medium">Offline</span>
            </>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Store Information */}
        <Card className="bg-gray-800 border-gray-700">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <Server className="w-5 h-5" />
              Store Information
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-gray-400 text-sm">Store Code</p>
                <p className="text-white font-bold text-lg">{storeInfo?.store_code}</p>
              </div>
              <div>
                <p className="text-gray-400 text-sm">Store Name</p>
                <p className="text-white font-medium">{storeInfo?.store_name}</p>
              </div>
            </div>
            <div>
              <p className="text-gray-400 text-sm">HQ Server URL</p>
              <p className="text-gray-300 text-sm font-mono">{storeInfo?.hq_url}</p>
            </div>
            <div>
              <p className="text-gray-400 text-sm">Last Sync</p>
              <p className="text-gray-300">
                {storeInfo?.last_sync_at 
                  ? new Date(storeInfo.last_sync_at).toLocaleString()
                  : 'Never'}
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Sync Status */}
        <Card className="bg-gray-800 border-gray-700">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <RefreshCw className="w-5 h-5" />
              Sync Status
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="bg-gray-700/50 rounded-lg p-3">
                <p className="text-gray-400 text-sm">Pending Sales</p>
                <p className="text-white font-bold text-2xl">{syncStatus?.pending_sales || 0}</p>
              </div>
              <div className="bg-gray-700/50 rounded-lg p-3">
                <p className="text-gray-400 text-sm">Pending Registers</p>
                <p className="text-white font-bold text-2xl">{syncStatus?.pending_registers || 0}</p>
              </div>
            </div>

            <div className="flex gap-2">
              <Button
                className="flex-1 bg-blue-600 hover:bg-blue-700"
                onClick={handleSyncPull}
                disabled={syncing || !syncStatus?.is_online}
              >
                <RefreshCw className={`w-4 h-4 mr-2 ${syncing ? 'animate-spin' : ''}`} />
                Pull from HQ
              </Button>
              <Button
                className="flex-1 bg-green-600 hover:bg-green-700"
                onClick={handleSyncPush}
                disabled={syncing || !syncStatus?.is_online}
              >
                <RefreshCw className={`w-4 h-4 mr-2 ${syncing ? 'animate-spin' : ''}`} />
                Push to HQ
              </Button>
            </div>

            {!syncStatus?.is_online && (
              <p className="text-yellow-400 text-sm text-center">
                Sync disabled - Cannot connect to HQ server
              </p>
            )}
          </CardContent>
        </Card>

        {/* Terminal Management */}
        <Card className="bg-gray-800 border-gray-700 lg:col-span-2">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="text-white flex items-center gap-2">
                <Monitor className="w-5 h-5" />
                Terminals ({terminals.length}/5)
              </CardTitle>
              <Button
                onClick={() => setShowAddModal(true)}
                disabled={terminals.length >= 5}
                className="bg-blue-600 hover:bg-blue-700"
              >
                <Plus className="w-4 h-4 mr-2" />
                Add Terminal
              </Button>
            </div>
            <CardDescription className="text-gray-400">
              Register and manage POS terminals for this store
            </CardDescription>
          </CardHeader>
          <CardContent>
            {terminals.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <Monitor className="w-12 h-12 mx-auto mb-3 opacity-50" />
                <p>No terminals registered</p>
                <p className="text-sm">Click "Add Terminal" to register your first terminal</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {terminals.map((terminal) => (
                  <div
                    key={terminal.terminal_id}
                    className="bg-gray-700/50 rounded-lg p-4 border border-gray-600"
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-lg bg-blue-500/20 flex items-center justify-center">
                          <Monitor className="w-5 h-5 text-blue-400" />
                        </div>
                        <div>
                          <p className="text-white font-bold">{terminal.terminal_id}</p>
                          <p className="text-gray-400 text-sm">{terminal.terminal_name}</p>
                        </div>
                      </div>
                      {terminal.current_register_id && (
                        <Badge className="bg-green-500/20 text-green-400 border-green-500/30">
                          Active
                        </Badge>
                      )}
                    </div>
                    <div className="mt-3 pt-3 border-t border-gray-600 flex justify-between items-center">
                      <span className="text-xs text-gray-500 capitalize">
                        Type: {terminal.terminal_type}
                      </span>
                      <span className="text-xs text-gray-500">
                        #{terminal.terminal_number}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Add Terminal Modal */}
      <Dialog open={showAddModal} onOpenChange={setShowAddModal}>
        <DialogContent className="bg-gray-800 border-gray-700 text-white">
          <DialogHeader>
            <DialogTitle>Register New Terminal</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label className="text-gray-300">Terminal Name</Label>
              <Input
                value={newTerminalName}
                onChange={(e) => setNewTerminalName(e.target.value)}
                placeholder="e.g., Counter 1, Checkout A"
                className="bg-gray-700 border-gray-600 text-white placeholder:text-gray-500"
                data-testid="terminal-name-input"
              />
              <p className="text-xs text-gray-500 mt-1">
                A friendly name to identify this terminal
              </p>
            </div>

            <div>
              <Label className="text-gray-300">Terminal Type</Label>
              <Select value={newTerminalType} onValueChange={setNewTerminalType}>
                <SelectTrigger className="bg-gray-700 border-gray-600 text-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-gray-700 border-gray-600">
                  <SelectItem value="pos">POS Counter</SelectItem>
                  <SelectItem value="kiosk">Self-Service Kiosk</SelectItem>
                  <SelectItem value="mobile">Mobile Terminal</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="bg-gray-700/50 rounded-lg p-3">
              <p className="text-sm text-gray-400">
                Terminal ID will be auto-generated as: 
                <span className="text-blue-400 font-mono ml-1">
                  {storeInfo?.store_code}-T{terminals.length + 1}
                </span>
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowAddModal(false)}
              className="border-gray-600 text-gray-300 hover:bg-gray-700"
            >
              Cancel
            </Button>
            <Button
              onClick={handleAddTerminal}
              disabled={adding}
              className="bg-blue-600 hover:bg-blue-700"
              data-testid="confirm-add-terminal-btn"
            >
              {adding ? 'Registering...' : 'Register Terminal'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default LocalStoreSettings;
